import React from 'react';
import { ArrowRight, Phone, Mail, MessageCircle, ExternalLink, Send, ChevronRight, Sparkles } from 'lucide-react';
import { ButtonActionConfig } from '../../types';
import { getOptimalTextColor } from '../../utils/contrast';

export interface WebsiteButtonProps {
  label: string;
  action?: ButtonActionConfig;
  // Shortcut props
  actionType?: 'page' | 'section' | 'url' | 'phone' | 'whatsapp' | 'email' | 'form' | 'service';
  target?: string;
  targetPage?: string;
  targetSection?: string;
  externalUrl?: string;
  phone?: string;
  whatsapp?: string;
  email?: string;
  serviceSlug?: string;
  // Styling
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'white';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
  accentColor?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  // Handlers
  onNavigate?: (slugOrPath: string) => void;
  onClick?: () => void;
  isEditor?: boolean;
  onEditAction?: () => void;
}

export const WebsiteButton: React.FC<WebsiteButtonProps> = ({
  label,
  action,
  actionType = action?.type || 'page',
  target = action?.target,
  targetPage = action?.targetPage || target,
  targetSection = action?.targetSection || target,
  externalUrl = action?.externalUrl || target,
  phone = action?.phone || target,
  whatsapp = action?.whatsapp || target,
  email = action?.email || target,
  serviceSlug = action?.serviceSlug || target,
  variant = 'primary',
  size = 'md',
  className = '',
  style = {},
  accentColor,
  icon,
  iconPosition = 'right',
  onNavigate,
  onClick,
  isEditor = false,
  onEditAction,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick();
      return;
    }

    const type = actionType;

    switch (type) {
      case 'page': {
        const slug = targetPage || '/';
        if (onNavigate) {
          onNavigate(slug.startsWith('/') ? slug : `/${slug}`);
        } else {
          window.location.hash = slug.startsWith('/') ? slug : `/${slug}`;
        }
        break;
      }
      case 'section': {
        const sectionId = (targetSection || '').replace(/^#/, '');
        if (sectionId) {
          const el = document.getElementById(sectionId) || document.querySelector(`[data-section-id="${sectionId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            window.location.hash = `#${sectionId}`;
          }
        }
        break;
      }
      case 'url': {
        if (externalUrl) {
          const validUrl = externalUrl.startsWith('http') ? externalUrl : `https://${externalUrl}`;
          window.open(validUrl, '_blank', 'noopener,noreferrer');
        }
        break;
      }
      case 'phone': {
        if (phone) {
          window.location.href = `tel:${phone.replace(/[^0-9+]/g, '')}`;
        }
        break;
      }
      case 'whatsapp': {
        if (whatsapp) {
          const cleanWa = whatsapp.replace(/[^0-9]/g, '');
          window.open(`https://wa.me/${cleanWa}`, '_blank', 'noopener,noreferrer');
        }
        break;
      }
      case 'email': {
        if (email) {
          window.location.href = `mailto:${email}`;
        }
        break;
      }
      case 'form': {
        const contactEl = document.getElementById('contact') || document.querySelector('[data-section-type="CONTACT"]');
        if (contactEl) {
          contactEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (onNavigate) {
          onNavigate('/iletisim');
        }
        break;
      }
      case 'service': {
        const sSlug = serviceSlug || 'danismanlik';
        if (onNavigate) {
          onNavigate(`/hizmetler/${sSlug}`);
        }
        break;
      }
      default: {
        if (onNavigate && target) {
          onNavigate(target);
        }
      }
    }
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-3.5 py-1.5 text-xs rounded-lg gap-1.5',
    md: 'px-5 py-2.5 text-xs sm:text-sm font-bold rounded-xl gap-2',
    lg: 'px-7 py-3.5 text-sm sm:text-base font-extrabold rounded-2xl gap-2.5 shadow-lg',
  }[size];

  // Base variant styles
  let variantStyle: React.CSSProperties = {};
  let variantClasses = '';

  if (variant === 'primary') {
    const btnBg = (style?.backgroundColor as string) || accentColor || '#2563EB';
    const btnText = (style?.color as string) || getOptimalTextColor(btnBg);
    variantClasses = 'shadow-md hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200';
    variantStyle = {
      backgroundColor: btnBg,
      color: btnText,
      ...style,
    };
  } else if (variant === 'secondary') {
    variantClasses = 'bg-slate-800 hover:bg-slate-700 text-white shadow-sm hover:scale-[1.02] active:scale-[0.98] transition-all duration-200';
    variantStyle = { ...style };
  } else if (variant === 'outline') {
    const outlineColor = accentColor || '#2563EB';
    variantClasses = 'border-2 bg-transparent hover:bg-white/10 active:scale-[0.98] transition-all duration-200';
    variantStyle = {
      borderColor: outlineColor,
      color: outlineColor,
      ...style,
    };
  } else if (variant === 'white') {
    variantClasses = 'bg-white hover:bg-slate-50 text-slate-900 shadow-md hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200';
    variantStyle = { color: '#0F172A', ...style };
  } else {
    variantClasses = 'bg-transparent hover:bg-white/10 text-inherit transition-all duration-200';
    variantStyle = { ...style };
  }

  // Default icon based on actionType
  const renderIcon = () => {
    if (icon) return icon;
    switch (actionType) {
      case 'phone':
        return <Phone className="w-4 h-4 shrink-0" />;
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4 shrink-0 text-emerald-400" />;
      case 'email':
        return <Mail className="w-4 h-4 shrink-0" />;
      case 'url':
        return <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-80" />;
      case 'form':
        return <Send className="w-3.5 h-3.5 shrink-0" />;
      default:
        return <ArrowRight className="w-4 h-4 shrink-0 transition-transform group-hover:translate-x-1" />;
    }
  };

  return (
    <div className="relative inline-flex group items-center">
      <button
        type="button"
        onClick={handleClick}
        style={variantStyle}
        className={`inline-flex items-center justify-center font-bold tracking-tight select-none cursor-pointer ${sizeClasses} ${variantClasses} ${className}`}
      >
        {iconPosition === 'left' && renderIcon()}
        <span>{label}</span>
        {iconPosition === 'right' && renderIcon()}
      </button>

      {/* Editor Quick Config Trigger */}
      {isEditor && onEditAction && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEditAction();
          }}
          title="Buton Aksiyonunu Düzenle"
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-slate-900 text-blue-400 border border-blue-500 shadow-md flex items-center justify-center text-[10px] hover:scale-110 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          ⚙️
        </button>
      )}
    </div>
  );
};
